import Timer20 from '@carbon/icons-react/es/timer/20';
import { Tooltip } from 'antd';
import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { User } from 'api/models';
import { UserTimesheetPreview } from 'api/notifications';
import { SquadAvatarList } from 'components/atoms/squad-avatar-list';
import {
  useGetAllActiveUsersMap,
  useGetUserFromIndexedDb,
} from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { fullName } from 'utils/full-name';
import { Header } from './components';
import styles from './group.module.css';

interface Props {
  timesheet: UserTimesheetPreview;
}

const Icon = styled(Timer20)`
  margin-right: var(--spacing-04);
  color: var(--blue-60);
`;

export const UserTimesheetHeader: FC<Props> = ({
  timesheet: { userId, approverIds },
  children,
}) => {
  const {
    user: { userId: currentUserId },
  } = useUser();
  const users = useGetAllActiveUsersMap();
  const user = useGetUserFromIndexedDb(userId) as User;
  const name = useTranslation('notifications.messages.timesheet', {
    same: currentUserId === userId,
    user: user == null ? '' : fullName(user),
  });
  const approverTitle = useTranslation('approvals.approvers');
  const approvers = useMemo(
    () => approverIds.map((id) => users[id]) as User[],
    [approverIds, users],
  );

  const subtitle = useTranslation('settings.timesheets.title');

  return (
    <Header className={styles.header}>
      <Header.Primary>
        <Icon />
        <Header.PrimaryBlock>
          <div title={name} className={`${styles.title} ${styles.ellipsis}`}>
            {name}
          </div>
          <div
            title={subtitle}
            className={`${styles.subtitle} ${styles.ellipsis}`}
          >
            {subtitle}
          </div>
        </Header.PrimaryBlock>
      </Header.Primary>
      {approvers.length > 0 ? (
        <Header.Secondary>
          <Tooltip title={approverTitle}>
            <SquadAvatarList squad={{ users: approvers, teams: [] }} />
          </Tooltip>
        </Header.Secondary>
      ) : null}
      <Header.Hanger>{children}</Header.Hanger>
    </Header>
  );
};
